import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { InvestigationBreadcrumbChipConnected } from '@/components/investigations/InvestigationBreadcrumbChipConnected'
import { AiButton } from '@/components/ui/ai-button'
import { WhatsNewLauncher } from '@/features/whats-new/WhatsNewLauncher'
import { cn } from '@/utils/cn'
import { useAgentSidebar } from './AgentSidebarContext'
import { showsAssistantLauncher } from './assistant-launcher'
import { MobileNav } from './MobileNav'
import { HOME_ROUTE } from './sections'
import { useToolbarSlot } from './ToolbarContext'
import { useActiveNav } from './useActiveNav'
import { type Crumb, useBreadcrumb } from './useBreadcrumb'

function BreadcrumbView({
  crumbs,
  tailFollows = false,
  leadingSeparator = false,
  className,
}: {
  crumbs: Crumb[]
  /** More path (chip + view crumbs) follows — don't style the last crumb as the leaf. */
  tailFollows?: boolean
  /** Render a separator before the first crumb (continuation after the chip). */
  leadingSeparator?: boolean
  className?: string
}) {
  return (
    <div className={cn('flex min-w-0 items-center text-xs text-muted-foreground', className)}>
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1 && !tailFollows
        const node = isLast ? (
          <span className="max-w-[28rem] truncate font-medium text-foreground" title={c.label}>
            {c.label}
          </span>
        ) : c.href ? (
          <Link to={c.href} className="transition-colors hover:text-foreground">
            {c.label}
          </Link>
        ) : (
          <span>{c.label}</span>
        )
        // Below md only the leaf fits: ancestors go, and with them the
        // separator that would then lead nothing.
        return (
          <span
            key={`${i}-${c.label}`}
            className={cn('min-w-0 items-center', isLast ? 'flex' : 'hidden md:flex')}
          >
            {(i > 0 || leadingSeparator) && (
              <span className={cn('mx-1 opacity-60', !leadingSeparator && 'hidden md:inline')}>
                /
              </span>
            )}
            {node}
          </span>
        )
      })}
    </div>
  )
}

export function TopBar() {
  const { t } = useTranslation()
  const { caseId, pathname } = useActiveNav()
  const agent = useAgentSidebar()
  const toolbar = useToolbarSlot()
  const { crumbs, viewTail, leafRepeatsChip } = useBreadcrumb()

  return (
    // Below md the toolbar actions wrap onto a second row that scrolls
    // sideways on its own.
    <div className="flex min-h-shell-header shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-border bg-card px-2 py-1.5 md:h-shell-header md:flex-nowrap md:gap-x-3 md:px-5 md:py-0">
      <MobileNav />
      {/* On investigation-scoped view routes the chip sits INSIDE the path:
          `Cases / case / [chip] / Graph`. Everywhere else it trails the
          breadcrumb as the standalone viewpoint indicator (unchanged). */}
      <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-initial">
        <BreadcrumbView
          crumbs={crumbs}
          tailFollows={viewTail.length > 0}
          // Below md only the leaf shows, and here the chip already says it.
          className={leafRepeatsChip ? 'max-md:hidden' : undefined}
        />
        {viewTail.length > 0 && (
          <span className="hidden text-xs text-muted-foreground opacity-60 md:inline">/</span>
        )}
        <InvestigationBreadcrumbChipConnected />
        {viewTail.length > 0 && <BreadcrumbView crumbs={viewTail} leadingSeparator />}
      </div>

      <div className="hidden flex-1 md:block" />

      <div className="contents md:flex md:items-center md:gap-2">
        {toolbar && (
          <div className="order-last flex w-full items-center gap-2 overflow-x-auto scrollbar-none empty:hidden md:contents">
            {toolbar}
          </div>
        )}
        <WhatsNewLauncher autoOpen={pathname === HOME_ROUTE} />
        {showsAssistantLauncher(pathname, caseId) && (
          <AiButton
            assistant
            emphasis="neon"
            size="xs"
            onClick={agent.toggle}
            aria-pressed={agent.open}
            aria-label={t('common.askSpectre')}
            className="shrink-0 rounded-full max-md:size-9 max-md:gap-0 max-md:px-0"
          >
            <span className="max-md:sr-only">{t('common.askSpectre')}</span>
          </AiButton>
        )}
      </div>
    </div>
  )
}
